import { useNavigate, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight, CheckCircle2 } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { PageHeader } from "@/components/library/layout";
import { StatsGrid } from "@/components/library/stats";
import { ActivityFeed } from "@/components/library/collab";
import { Pill } from "@/components/shared/ui";
import Icon from "@/components/Icon";
import { useStore, stats, list, DEPARTMENTS, OWNER_ONLY_SLUGS, deptChecklistStatus, todayTodos, departmentBySlug, lagerSummary, activityFeed } from "@/lib/dataStore";

const KIND_ICON = { checklist: "ClipboardList", incident: "AlertTriangle", task: "CheckSquare" };
const KIND_TONE = { checklist: "bg-accent/15 text-accent", incident: "bg-danger/10 text-danger", task: "bg-secondary text-muted-foreground" };
const KIND_TAB = { checklist: "checklistor", incident: "problem", task: "uppgifter" };

export default function Home() {
  useStore();
  const { currentRestaurant, profile, role, roleLabel, currentMembership, hasPermission } = useSbAuth();
  const nav = useNavigate();
  if (hasPermission("dashboard.access")) return <Navigate to="/konto/ledning" replace />;
  const rid = currentRestaurant?.id;
  const s = rid ? stats(rid) : {};
  const audit = rid ? activityFeed(rid, currentMembership?.department || null, 8) : [];

  // staff see only their department; owner-only depts hidden unless permitted
  const myDept = currentMembership?.department;
  const depts = (myDept ? DEPARTMENTS.filter((d) => d.slug === myDept || d.slug === "kunskap") : DEPARTMENTS)
    .filter((d) => !OWNER_ONLY_SLUGS.includes(d.slug) || hasPermission("foodcost.access"));
  const todos = rid ? todayTodos(rid).filter((t) => !myDept || t.department === myDept) : [];

  const kpis = [
    { label: "Klart idag", value: `${s.today_completion || 0}%`, tone: "success", testid: "kpi-klart", onClick: () => nav("/konto/operation") },
    { label: "Öppna problem", value: s.open_incidents || 0, tone: s.open_incidents ? "danger" : undefined, testid: "kpi-problem", onClick: () => nav("/konto/operation") },
    { label: "Uppgifter", value: s.tasks || 0, testid: "kpi-uppgifter", onClick: () => nav("/konto/operation") },
    { label: "Manualer", value: s.manuals || 0, testid: "kpi-manualer", onClick: () => nav("/konto/operation/kunskap") },
  ];

  return (
    <div className="max-w-[1100px] mx-auto p-6 sm:p-10 animate-fade-up" data-testid="home-page">
      <PageHeader overline={currentRestaurant?.name}
        title={`God dag, ${profile?.full_name?.split(" ")[0] || ""}`}
        description={`Inloggad som ${roleLabel}. Här är läget i verksamheten just nu.`} />

      <StatsGrid items={kpis} />

      <button onClick={() => window.dispatchEvent(new Event("open-assistant"))} data-testid="home-assistant-card"
        className="mt-8 w-full text-left group flex items-center gap-4 bg-foreground text-background rounded-2xl p-5 hover:-translate-y-0.5 transition-transform duration-300">
        <span className="h-11 w-11 grid place-items-center rounded-xl bg-background/15 shrink-0"><Icon name="Bot" className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold tracking-tight">Fråga Assistenten</p>
          <p className="text-sm text-background/70 truncate">Hur gör jag pizzasallad? Hur många gram ost på Vesuvio? Vad gör jag om en kund klagar?</p>
        </div>
        <ArrowUpRight className="h-5 w-5 shrink-0 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
      </button>


      <div className="mt-8 bg-card border border-border rounded-2xl overflow-hidden" data-testid="todo-today">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <p className="font-display font-bold tracking-tight">Att göra idag</p>
          {todos.length > 0 && <span data-testid="todo-count" className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent/15 text-accent">{todos.length}</span>}
        </div>
        {todos.length === 0 ? (
          <div className="p-8 text-center" data-testid="todo-empty">
            <CheckCircle2 className="h-8 w-8 mx-auto text-[hsl(145_60%_60%)] mb-2" />
            <p className="text-sm text-muted-foreground">Allt klart för idag – inga öppna checklistor, problem eller uppgifter.</p>
          </div>
        ) : (
          <div className="divide-y divide-border max-h-[380px] overflow-y-auto">
            {todos.slice(0, 12).map((t) => (
              <button key={t.id} data-testid={`todo-${t.id}`} onClick={() => nav(`/konto/operation/${t.department}`, { state: { tab: KIND_TAB[t.kind] } })}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-secondary/50 transition-colors">
                <span className={`h-8 w-8 grid place-items-center rounded-lg shrink-0 ${KIND_TONE[t.kind]}`}><Icon name={KIND_ICON[t.kind]} className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{departmentBySlug(t.department)?.name} · {t.meta}</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            ))}
            {todos.length > 12 && <p className="px-5 py-3 text-xs text-muted-foreground">+ {todos.length - 12} till…</p>}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2">
          <p className="overline text-muted-foreground mb-4">{myDept ? "Din avdelning" : "Avdelningar"}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {depts.map((d) => {
              const cs = rid ? deptChecklistStatus(rid, d.slug) : { total: 0, open: 0 };
              return (
              <button key={d.slug} onClick={() => nav(d.slug === "foodcost" ? "/konto/foodcost" : `/konto/operation/${d.slug}`)} data-testid={`home-dept-${d.slug}`}
                className="group text-left bg-card border border-border rounded-2xl p-5 hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all duration-300">
                <div className="flex items-start justify-between">
                  <span className="h-10 w-10 grid place-items-center rounded-xl text-white" style={{ background: d.color }}><Icon name={d.icon} className="h-5 w-5" strokeWidth={2} /></span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>
                <h3 className="font-display font-bold text-lg mt-4 tracking-tight">{d.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{d.description}</p>
                {d.slug === "lager" ? (
                  (() => { const up = rid ? lagerSummary(rid).underPar : 0; return up > 0 && (
                    <div className="mt-4"><Pill tone="warning" testid="home-lager-underpar">{up} under par-nivå</Pill></div>
                  ); })()
                ) : cs.total > 0 && (
                  <div className="mt-4">
                    {cs.open > 0
                      ? <Pill tone="warning" testid={`home-checklist-${d.slug}`}>{cs.open} checklist{cs.open > 1 ? "or" : "a"} kvar idag</Pill>
                      : <Pill tone="success" testid={`home-checklist-${d.slug}`}>Alla checklistor klara</Pill>}
                  </div>
                )}
              </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="overline text-muted-foreground mb-4">Senaste aktivitet</p>
          <ActivityFeed items={audit} />
        </div>
      </div>
    </div>
  );
}
